/**
 * StaffAvatar — ảnh tròn nhỏ cạnh tên nhân sự, dùng CHUNG toàn app.
 * Ưu tiên staffId; fallback match tên duy nhất; cuối cùng initials + tint.
 */
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/avatar";
import { cn } from "@/lib/utils";
import {
  avatarSrcFor,
  avatarTint,
  normalizeName,
  staffInitials,
  useStaffAvatarMap,
} from "@/lib/staff-avatar";
import { useEquippedFor } from "@/features/cosmetics/api";
import { RankFrame } from "@/features/cosmetics/RankFrame";

// `| undefined` là bắt buộc: dự án bật exactOptionalPropertyTypes, nên caller
// truyền một biến có thể undefined (VD me?.id) sẽ bị từ chối nếu thiếu.
type Props = {
  staffId?: string | null | undefined;
  name: string;
  sizePx?: number | undefined;
  className?: string | undefined;
  /** Ảnh trần, không khung xếp hạng — ô chọn người, màn khách (CEO 10a). */
  plain?: boolean | undefined;
  /** "full": nơi đặt đã chừa lề cho cánh + vương miện tràn ra (bục, Bộ sưu tập). */
  frameSize?: "full" | "compact" | undefined;
  /**
   * Trang Xếp hạng (CEO 8a·11a, 19/10/2026): kim loại theo HẠNG đang đứng
   * (1 vàng · 2 bạc · 3 đồng), giữ kiểu dáng người đó chọn, ngọc nhóm "Giữ
   * ngôi" — kể cả người chọn "Không đeo". Vắng = theo món đang đeo như cũ.
   */
  rankTier?: 1 | 2 | 3 | undefined;
};

const KIM_HANG = ["gold", "silver", "bronze"] as const;

export function StaffAvatar({ staffId, name, sizePx = 24, className, plain, frameSize, rankTier }: Props) {
  const { byId, byName } = useStaffAvatarMap();
  const rec = (staffId ? byId[staffId] : undefined) ?? byName[normalizeName(name)];
  const src = avatarSrcFor(rec?.avatarUrl, sizePx);
  // Khung đi theo ảnh ở mọi màn (CEO 5b, 17/10/2026). Không đeo / `plain` ⇒ y như cũ.
  const eq = useEquippedFor(plain ? undefined : (staffId ?? rec?.id));
  const frame = rankTier
    ? { code: `reign-${KIM_HANG[rankTier - 1]}`, tier: rankTier, group: "reign" }
    : eq?.frame;
  // Có khung: lớp của nơi gọi (lề, căn dòng) dời ra hộp khung bên ngoài.
  const anh = (
    <Avatar className={cn("shrink-0", frame ? undefined : className)} style={{ width: sizePx, height: sizePx }}>
      {src ? <AvatarImage src={src} alt={name} /> : null}
      <AvatarFallback
        style={{ background: avatarTint(name), fontSize: Math.max(9, sizePx * 0.38) }}
        className="font-semibold text-black/70"
      >
        {staffInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
  if (!frame && !eq?.craftTier) return anh;
  return (
    <RankFrame
      frame={frame}
      style={eq?.style}
      craftTier={eq?.craftTier}
      sizePx={sizePx}
      className={frame ? className : undefined}
      ban={frameSize === "full" ? "du" : frameSize === "compact" ? "gon" : undefined}
    >
      {anh}
    </RankFrame>
  );
}
